<template>
  <main class="regal-coming-soon">
    <!-- Gold spotlight + vignette ambience -->
    <div class="rcs-spotlight" aria-hidden="true"></div>
    <div class="rcs-vignette" aria-hidden="true"></div>

    <header class="rcs-top">
      <img
        class="rcs-mark"
        :src="lockup"
        alt="REGAL — Luxury Technology"
        width="280"
        height="60"
      />
    </header>

    <section class="rcs-stage">
      <!-- Left column: the message -->
      <div class="rcs-col rcs-col-text">
        <p class="regal-eyebrow rcs-eyebrow">Reserved for Visionaries</p>

        <h1 class="rcs-headline">
          Command the<br />
          <span class="regal-foil-text">Future.</span>
        </h1>

        <p class="rcs-lede">
          A new standard in luxury technology is being forged. REGAL delivers premium
          platforms designed to elevate the influence, efficiency, and competitive
          advantage of those who lead.
        </p>

        <!-- Countdown to launch -->
        <ul class="rcs-countdown" aria-label="Time until launch">
          <li v-for="unit in countdown" :key="unit.label" class="rcs-count">
            <span class="rcs-count-value">{{ unit.value }}</span>
            <span class="rcs-count-label">{{ unit.label }}</span>
          </li>
        </ul>
      </div>

      <!-- Right column: the crest -->
      <div class="rcs-col rcs-col-visual">
        <img
          class="rcs-crest"
          :src="crest"
          alt="REGAL royal crest"
          width="220"
          height="260"
        />
      </div>
    </section>

    <footer class="rcs-foot">
      <hr class="regal-hairline rcs-rule" />
      <p class="rcs-foot-text">Technology Crafted for Leaders</p>
      <p class="rcs-foot-meta">&copy; {{ year }} REGAL · Excellence Without Compromise</p>
    </footer>
  </main>
</template>

<script>
import lockup from '@/assets/brand/regal-lockup.svg'
import crest from '@/assets/brand/regal-crest.svg'

// Fixed launch target — adjust as the timeline firms up.
const LAUNCH_DATE = new Date('2026-09-01T09:00:00Z')

export default {
  name: 'ComingSoonView',
  data () {
    return {
      lockup,
      crest,
      now: new Date(),
      timer: null
    }
  },
  computed: {
    year () {
      return new Date().getFullYear()
    },
    countdown () {
      const diff = Math.max(0, LAUNCH_DATE - this.now)
      const sec = Math.floor(diff / 1000)
      const pad = (n) => String(n).padStart(2, '0')
      return [
        { label: 'Days', value: pad(Math.floor(sec / 86400)) },
        { label: 'Hours', value: pad(Math.floor((sec % 86400) / 3600)) },
        { label: 'Minutes', value: pad(Math.floor((sec % 3600) / 60)) },
        { label: 'Seconds', value: pad(sec % 60) }
      ]
    }
  },
  mounted () {
    this.timer = setInterval(() => {
      this.now = new Date()
    }, 1000)
  },
  beforeUnmount () {
    clearInterval(this.timer)
  }
}
</script>

<style scoped>
.regal-coming-soon {
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--space-7) var(--gutter) var(--space-8);
  background: var(--gradient-dark);
  overflow: hidden;
}

.rcs-spotlight,
.rcs-vignette {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.rcs-spotlight { background: var(--gradient-spotlight); }
.rcs-vignette { background: var(--gradient-vignette); }

/* ---- Top brand lockup ----------------------------------------- */
.rcs-top {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: var(--container-xl);
  display: flex;
  justify-content: center;
  padding-block: var(--space-2) var(--space-9);
}
.rcs-mark { height: auto; opacity: 0.96; }

/* ---- Centre stage: two columns -------------------------------- */
.rcs-stage {
  position: relative;
  z-index: 1;
  flex: 1;
  width: 100%;
  max-width: var(--container-xl);
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  align-items: center;
  gap: var(--space-10);
}

.rcs-col-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
}

.rcs-col-visual {
  display: flex;
  justify-content: center;
  align-items: center;
}

.rcs-eyebrow {
  margin-bottom: var(--space-5);
  animation: rcs-fade var(--dur-slow) var(--ease-luxe) 120ms both;
}

.rcs-headline {
  font-size: clamp(var(--text-4xl), 7vw, var(--text-5xl));
  font-weight: var(--fw-semibold);
  letter-spacing: var(--ls-display);
  line-height: var(--lh-tight);
  margin-bottom: var(--space-5);
  animation: rcs-rise var(--dur-cinema) var(--ease-luxe) 160ms both;
}

.rcs-lede {
  max-width: 52ch;
  color: var(--text-secondary);
  font-size: var(--text-md);
  line-height: var(--lh-relaxed);
  margin-bottom: var(--space-8);
  animation: rcs-fade var(--dur-slow) var(--ease-luxe) 240ms both;
}

.rcs-crest {
  height: auto;
  width: clamp(160px, 22vw, 240px);
  filter: drop-shadow(0 16px 50px rgba(212, 175, 55, 0.20));
  animation: rcs-rise var(--dur-cinema) var(--ease-luxe) both;
}

/* ---- Countdown ------------------------------------------------ */
.rcs-countdown {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  gap: var(--space-4);
  animation: rcs-fade var(--dur-slow) var(--ease-luxe) 320ms both;
}
.rcs-count {
  min-width: 84px;
  padding: var(--space-4) var(--space-3);
  background: var(--surface-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md), var(--inset-hairline);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  text-align: center;
}
.rcs-count-value {
  font-family: var(--font-display);
  font-weight: var(--fw-semibold);
  font-size: var(--text-2xl);
  letter-spacing: var(--ls-display);
  color: var(--accent);
  font-variant-numeric: tabular-nums;
}
.rcs-count-label {
  font-size: var(--text-2xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--ls-wide);
  text-transform: uppercase;
  color: var(--text-muted);
}

/* ---- Footer --------------------------------------------------- */
.rcs-foot {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: var(--container-xl);
  margin-top: var(--space-9);
  text-align: center;
}
.rcs-rule { margin-bottom: var(--space-5); }
.rcs-foot-text {
  font-family: var(--font-editorial);
  font-style: italic;
  font-size: var(--text-lg);
  color: var(--text-secondary);
  margin-bottom: var(--space-2);
}
.rcs-foot-meta {
  font-size: var(--text-2xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--ls-wide);
  text-transform: uppercase;
  color: var(--text-muted);
}

/* ---- Motion --------------------------------------------------- */
@keyframes rcs-rise {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes rcs-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* ---- Responsive: collapse to one column ----------------------- */
@media (max-width: 860px) {
  .rcs-stage {
    grid-template-columns: 1fr;
    gap: var(--space-8);
    text-align: center;
  }
  .rcs-col-text { align-items: center; text-align: center; }
  .rcs-col-visual { order: -1; }
  .rcs-lede { max-width: 54ch; }
}

@media (max-width: 560px) {
  .rcs-countdown { gap: var(--space-2); }
  .rcs-count { min-width: 0; flex: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .rcs-crest, .rcs-eyebrow, .rcs-headline, .rcs-lede,
  .rcs-countdown { animation: none; }
}
</style>
